/**
 * Logica SSO lato client e chiamate all'API, senza dipendenze da TeamsJS o dal
 * DOM: così è testabile e riutilizzabile (tab, popup, test).
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface SsoDeps {
  /** Token silenzioso dal client Teams (`authentication.getAuthToken`). */
  silent(): Promise<string>;
  /** Popup di fallback (`authentication.authenticate`). */
  popup(url: string): Promise<string>;
  /** Preleva il token lasciato dal popup (handoff in localStorage). */
  takeHandoff?(): string | null;
  /** Origine della pagina, usata per costruire l'URL del popup. */
  origin: string;
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'errore sconosciuto';
}

/** URL della pagina che avvia il popup di consenso. */
export function authStartUrl(origin: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}/auth-start.html`;
}

/**
 * Prova prima il token silenzioso (SSO "puro"), poi il popup.
 * Il silenzioso fallisce tipicamente quando l'utente o l'admin non hanno ancora
 * acconsentito allo scope: in quel caso serve l'interazione esplicita.
 */
export async function acquireApiToken(deps: SsoDeps): Promise<string> {
  let silentError: unknown;
  try {
    const token = await deps.silent();
    if (!token) throw new Error('token vuoto');
    return token;
  } catch (err) {
    silentError = err;
  }

  try {
    const result = await deps.popup(authStartUrl(deps.origin));
    // Percorso preferito: il popup ha lasciato il token nell'handoff.
    const handed = deps.takeHandoff?.() ?? null;
    if (handed) return handed;
    // Compatibilità: se il popup ha restituito direttamente un JWT, usalo.
    if (result && result.split('.').length === 3) return result;
    throw new Error('il popup non ha restituito alcun token');
  } catch (popupError) {
    throw new Error(
      `SSO non riuscito (silenzioso: ${describe(silentError)}; popup: ${describe(popupError)})`,
    );
  }
}

/** Base URL dell'API: vuota = stessa origine (proxy/statico), override con VITE_API_BASE. */
export function apiBase(env: Record<string, string | undefined>): string {
  return (env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '');
}

export interface ApiGetOptions {
  base?: string;
  fetchImpl?: typeof fetch;
}

/** GET autenticata; lancia ApiError con lo status HTTP in caso di errore. */
export async function apiGet<T>(
  path: string,
  token: string | null,
  options: ApiGetOptions = {},
): Promise<T> {
  const base = options.base ?? '';
  const doFetch = options.fetchImpl ?? fetch;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await doFetch(`${base}${path}`, { headers });
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body.error === 'string') detail = body.error;
    } catch {
      detail = await res.text().catch(() => '');
    }
    throw new ApiError(
      `API ${path} ha risposto ${res.status}${detail ? `: ${detail}` : ''}`,
      res.status,
    );
  }
  return (await res.json()) as T;
}

/** Identità restituita da GET /api/me. */
export interface Identity {
  oid: string | null;
  name: string | null;
  upn: string | null;
  tid: string | null;
  scopes: string[];
  mock: boolean;
}

/** Profilo restituito da GET /api/graph/profile. */
export interface ProfilePayload {
  profile: {
    id: string | null;
    displayName: string | null;
    mail: string | null;
    userPrincipalName: string | null;
    jobTitle: string | null;
    department: string | null;
    officeLocation: string | null;
  };
  mock: boolean;
}
